import { memo, useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Tooltip } from "@heroui/react";
import { LockIcon, PushPinIcon, ShareFatIcon } from "@phosphor-icons/react/dist/ssr";
import { useShallow } from "zustand/shallow";
import clsx from "clsx";

import { useBoundStore } from "@/app/store";
import { NoteTags } from "./NoteTags";
import { FetchedNote } from "@/services/api/fetchers/notes";
import { NoteAction } from "../../types";
import { EyeSlashIcon } from "@phosphor-icons/react";
import { Popover } from "@/shared/components/common/Popover";
import { ViewMode } from "@/app/store/deviceSlice";

const LONG_PRESS_DURATION = 350; // milliseconds

export interface NoteCardProps {
  actions: (note: FetchedNote) => NoteAction[];
  "data-note-index"?: number;
  className?: string;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  note: FetchedNote;
  onFocus?: () => void;
  onPress: (wasKeyboard: boolean, ctrlKey?: boolean, longPress?: boolean) => void;
  tabIndex?: number;
  viewMode?: ViewMode;
}

export const NoteCard = memo(function NoteCard({
  actions,
  className,
  "data-note-index": dataIndex,
  note,
  onFocus,
  onPress,
  isSelected = false,
  isSelectionMode = false,
  viewMode = "grid",
}: NoteCardProps) {
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [longPressTimeoutId, setLongPressTimeoutId] = useState<number | null>(null);
  const hasTags = note.tags.length > 0;

  const shouldRenderActions = useMemo(
    () => deviceType === "desktop" && (isFocused || isHovered) && !isSelectionMode,
    [deviceType, isFocused, isHovered, isSelectionMode]
  );

  const noteActions = useMemo(() => (shouldRenderActions ? actions(note) : []), [actions, note, shouldRenderActions]);

  // Close popover when shouldRenderActions becomes false
  useEffect(() => {
    if (!shouldRenderActions && openPopover) {
      setOpenPopover(null);
    }
  }, [shouldRenderActions, openPopover]);

  const handlePressCancel = useCallback(() => {
    // Clear timeout on cancel
    if (longPressTimeoutId) {
      clearTimeout(longPressTimeoutId);
      setLongPressTimeoutId(null);
    }

    setLongPressTriggered(false);
  }, [longPressTimeoutId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (longPressTimeoutId) {
        clearTimeout(longPressTimeoutId);
      }
    };
  }, [longPressTimeoutId]);

  // Add scroll listener to cancel long press when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (longPressTimeoutId) {
        handlePressCancel();
      }
    };

    if (longPressTimeoutId) {
      // Listen to scroll events on the document and any parent scrollable containers
      document.addEventListener("scroll", handleScroll, true);
      document.addEventListener("touchmove", handleScroll, true);

      return () => {
        document.removeEventListener("scroll", handleScroll, true);
        document.removeEventListener("touchmove", handleScroll, true);
      };
    }
  }, [longPressTimeoutId, handlePressCancel]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onPress(true, e.ctrlKey || e.metaKey);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (longPressTriggered) {
      setLongPressTriggered(false);
      return;
    }
    onPress(false, e.ctrlKey || e.metaKey);
  };

  const handlePressStart = () => {
    if (deviceType === "mobile") {
      setLongPressTriggered(false);

      // Clear any existing timeout
      if (longPressTimeoutId) {
        clearTimeout(longPressTimeoutId);
      }

      // Set timeout to trigger long press automatically
      const timeoutId = setTimeout(() => {
        setLongPressTriggered(true);
        onPress(false, false, true); // long press
      }, LONG_PRESS_DURATION);

      setLongPressTimeoutId(timeoutId);
    }
  };

  const handlePressEnd = () => {
    if (deviceType === "mobile") {
      // Clear the timeout if press ends before duration
      if (longPressTimeoutId) {
        clearTimeout(longPressTimeoutId);
        setLongPressTimeoutId(null);
      }

      // If long press was already triggered, prevent normal click
      if (longPressTriggered) {
        setLongPressTriggered(false);
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    handlePressCancel();
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  // This is being used to keep the focus state to be able to navigate with the keyboard
  // through actions
  const handleBlur = (e: React.FocusEvent) => {
    const isDescendant = (parent: HTMLElement, child: HTMLElement) => {
      let node = child.parentNode;
      while (node != null) {
        if (node === parent) {
          return true;
        }
        node = node.parentNode;
      }
      return false;
    };

    const currentTarget = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;

    if (!relatedTarget || !isDescendant(currentTarget, relatedTarget)) {
      setIsFocused(false);
    }
  };

  // Build a preview where the first line (before the first newline) is bold
  const previewHtml = useMemo(() => {
    if (!note.text_content) return "";
    const preview = note.text_content.substring(0, 250);
    const [firstLine, ...rest] = preview.split(/\r?\n/);
    const restJoined = note.masked ? "" : rest.filter(Boolean).join("<br>");
    return `<strong>${firstLine}</strong><br>${restJoined}`;
  }, [note.text_content, note.masked]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
      onTouchCancel={handlePressCancel}
      className={clsx(
        "e_li_readMode group border-default-200 bg-content1 focus-visible:ring-primary-500 dark:border-default-100 relative flex w-full cursor-pointer flex-col overflow-hidden rounded-2xl border shadow-sm focus:outline-none focus-visible:ring-2",
        {
          "h-64": viewMode === "grid",
          "h-16": viewMode === "list",
          "border-primary dark:border-primary border-3": isSelected,
        },
        className
      )}
      data-note-index={dataIndex}
    >
      <div className="relative flex-1 overflow-hidden px-3 py-1">
        <div className="absolute top-1 right-2 flex gap-1.5">
          {Boolean(note.public_uuid) && <ShareFatIcon size={12} weight="duotone" />}
          {note.pinned && <PushPinIcon size={12} weight="duotone" />}
          {note.locked && <LockIcon size={12} weight="duotone" />}
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: previewHtml }}
          className={clsx("pointer-events-none py-2 select-none", {
            "line-clamp-2 pt-2 pb-1 text-sm": viewMode === "list",
          })}
        />

        <AnimatePresence initial={false}>
          {note.masked && (
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2"
            >
              <EyeSlashIcon size={viewMode === "list" ? 32 : 56} weight="duotone" />
            </motion.span>
          )}
        </AnimatePresence>

        {viewMode === "grid" && (
          <div className="from-content1 absolute bottom-0 left-0 h-12 w-full bg-linear-to-t to-transparent" />
        )}
      </div>

      <div>
        {hasTags && (
          <NoteTags
            className={clsx("mx-3", { "py-2": viewMode === "grid", "py-1": viewMode === "list" })}
            tags={note.tags}
          />
        )}
        {deviceType === "desktop" && (
          <div
            className={clsx(
              "scrollbar-hide mx-3 flex justify-end gap-1 overflow-y-auto py-1 transition-[height,opacity] duration-200 ease-out",
              "h-0 opacity-0",
              {
                "h-10 opacity-100": shouldRenderActions,
              }
            )}
            tabIndex={-1}
          >
            {shouldRenderActions &&
              noteActions.map((action) =>
                action.content ? (
                  <Tooltip key={action.key} content={action.label} placement="bottom" size="sm" className="shrink-0">
                    <div tabIndex={-1}>
                      <Popover
                        icon={action.icon}
                        id={action.key}
                        externalState={{
                          isOpen: openPopover === action.key,
                          onOpenChange: (isOpen: boolean) => setOpenPopover(isOpen ? action.key : null),
                        }}
                        triggerProps={{ size: "sm" }}
                      >
                        <div className="max-h-60 w-full overflow-x-hidden overflow-y-auto">
                          {action.content(() => setOpenPopover(null))}
                        </div>
                      </Popover>
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip key={action.key} content={action.label} placement="bottom" size="sm" className="shrink-0">
                    <Button isIconOnly size="sm" variant="light" onPress={() => action.run?.(note)}>
                      {action.icon}
                    </Button>
                  </Tooltip>
                )
              )}
          </div>
        )}
      </div>
    </div>
  );
});

interface FocusableNoteCardProps {
  actions: (note: FetchedNote) => NoteAction[];
  index: number;
  isFocused: boolean;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  note: FetchedNote;
  onFocus: (index: number) => void;
  onPress: (id: number, wasKeyboard: boolean, ctrlKey?: boolean, longPress?: boolean) => void;
  viewMode?: ViewMode;
}

export const FocusableNoteCard = memo(function FocusableNoteCard({
  actions,
  index,
  isFocused,
  isSelected = false,
  isSelectionMode = false,
  note,
  onFocus,
  onPress,
  viewMode = "grid",
}: FocusableNoteCardProps) {
  return (
    <NoteCard
      actions={actions}
      className="note-card"
      data-note-index={index}
      isSelected={isSelected}
      isSelectionMode={isSelectionMode}
      note={note}
      tabIndex={isFocused ? 0 : -1}
      viewMode={viewMode}
      onFocus={() => onFocus(index)}
      onPress={(wasKeyboard, ctrlKey, longPress) => onPress(note.id, wasKeyboard, ctrlKey, longPress)}
    />
  );
});
